import type { MessageDescriptor } from 'react-intl-next';

import { backgroundPaletteTooltipMessages } from '@atlaskit/editor-common/ui-color';
import { token } from '@atlaskit/tokens';

const backgroundColorMessages = backgroundPaletteTooltipMessages.light as Record<
	string,
	MessageDescriptor
>;

export type TabColorAppearanceMode = 'legacy' | 'standard';

export type TabColorAppearance = {
	swatch: {
		backgroundColor: string;
		labelMessage: MessageDescriptor;
	};
	tabPill: {
		inactive: {
			backgroundColor: string;
			backgroundColorHovered: string;
			borderColor: string;
			textColor: string;
		};
		active: {
			backgroundColor: string;
			borderColor: string;
			textColor: string;
		};
	};
};

export type TabColorDefinition = {
	/** Hex value used by the Editor Panel and Table color contracts. */
	panelColor: string;
	/**
	 * Visual definitions for this persisted color key. The legacy appearance is
	 * present only when the legacy palette intentionally renders it differently.
	 */
	appearances: {
		standard: TabColorAppearance;
		legacy?: TabColorAppearance;
	};
};

export type TabColorKey =
	| 'blue-subtlest'
	| 'blue-subtler'
	| 'blue-subtle'
	| 'teal-subtlest'
	| 'teal-subtler'
	| 'teal-subtle'
	| 'green-subtlest'
	| 'green-subtler'
	| 'green-subtle'
	| 'lime-subtlest'
	| 'lime-subtler'
	| 'lime-subtle'
	| 'yellow-subtlest'
	| 'yellow-subtler'
	| 'yellow-subtle'
	| 'orange-subtlest'
	| 'orange-subtler'
	| 'orange-subtle'
	| 'red-subtlest'
	| 'red-subtler'
	| 'red-subtle'
	| 'magenta-subtlest'
	| 'magenta-subtler'
	| 'magenta-subtle'
	| 'purple-subtlest'
	| 'purple-subtler'
	| 'purple-subtle'
	| 'gray-subtler'
	| 'gray-subtle';

type TabColorAppearanceInput = {
	backgroundColor: string;
	backgroundColorHovered: string;
	borderColor: string;
	textColor: string;
	swatchBackgroundColor?: string;
	labelMessage: MessageDescriptor;
	activeBackgroundColor: string;
	activeBorderColor?: string;
	activeTextColor: string;
};

const createTabColorAppearance = ({
	backgroundColor,
	backgroundColorHovered,
	borderColor,
	textColor,
	swatchBackgroundColor = backgroundColor,
	labelMessage,
	activeBackgroundColor,
	activeBorderColor = borderColor,
	activeTextColor,
}: TabColorAppearanceInput): TabColorAppearance => ({
	swatch: {
		backgroundColor: swatchBackgroundColor,
		labelMessage,
	},
	tabPill: {
		inactive: {
			backgroundColor,
			backgroundColorHovered,
			borderColor,
			textColor,
		},
		active: {
			backgroundColor: activeBackgroundColor,
			borderColor: activeBorderColor,
			textColor: activeTextColor,
		},
	},
});

/**
 * Canonical catalog of persisted tab colors and every visual representation
 * derived from them.
 */
export const TAB_COLOR_DEFINITIONS: Record<TabColorKey, TabColorDefinition> = {
	'blue-subtlest': {
		panelColor: '#DEEBFF',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#DEEBFF'],
				backgroundColor: token('color.background.accent.blue.subtlest'),
				backgroundColorHovered: token('color.background.accent.blue.subtlest.hovered'),
				borderColor: token('color.border.accent.blue'),
				textColor: token('color.text.accent.blue'),
				activeBackgroundColor: token('color.background.accent.blue.subtler'),
				activeTextColor: token('color.text.accent.blue.bolder'),
			}),
		},
	},
	'blue-subtler': {
		panelColor: '#B3D4FF',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#B3D4FF'],
				backgroundColor: token('color.background.accent.blue.subtler'),
				backgroundColorHovered: token('color.background.accent.blue.subtler.hovered'),
				borderColor: token('color.border.accent.blue'),
				textColor: token('color.text.accent.blue'),
				activeBackgroundColor: token('color.background.accent.blue.subtle'),
				activeTextColor: token('color.text.accent.blue.bolder'),
			}),
		},
	},
	'blue-subtle': {
		panelColor: '#ADCBFB',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#ADCBFB'],
				backgroundColor: token('color.background.accent.blue.subtle'),
				backgroundColorHovered: token('color.background.accent.blue.subtle.hovered'),
				borderColor: token('color.border.accent.blue'),
				textColor: token('color.text.accent.blue.bolder'),
				activeBackgroundColor: token('color.background.accent.blue.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#ADCBFB'],
				backgroundColor: token('color.background.accent.blue.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.blue.subtlest.hovered'),
				borderColor: token('color.border.accent.blue'),
				textColor: token('color.text.accent.blue.bolder'),
				activeBackgroundColor: token('color.background.accent.blue.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'teal-subtlest': {
		panelColor: '#E6FCFF',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#E6FCFF'],
				backgroundColor: token('color.background.accent.teal.subtlest'),
				backgroundColorHovered: token('color.background.accent.teal.subtlest.hovered'),
				borderColor: token('color.border.accent.teal'),
				textColor: token('color.text.accent.teal'),
				activeBackgroundColor: token('color.background.accent.teal.subtler'),
				activeTextColor: token('color.text.accent.teal.bolder'),
			}),
		},
	},
	'teal-subtler': {
		panelColor: '#B3F5FF',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#B3F5FF'],
				backgroundColor: token('color.background.accent.teal.subtler'),
				backgroundColorHovered: token('color.background.accent.teal.subtler.hovered'),
				borderColor: token('color.border.accent.teal'),
				textColor: token('color.text.accent.teal'),
				activeBackgroundColor: token('color.background.accent.teal.subtle'),
				activeTextColor: token('color.text.accent.teal.bolder'),
			}),
		},
	},
	'teal-subtle': {
		panelColor: '#B1E4F7',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#B1E4F7'],
				backgroundColor: token('color.background.accent.teal.subtle'),
				backgroundColorHovered: token('color.background.accent.teal.subtle.hovered'),
				borderColor: token('color.border.accent.teal'),
				textColor: token('color.text.accent.teal.bolder'),
				activeBackgroundColor: token('color.background.accent.teal.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#B1E4F7'],
				backgroundColor: token('color.background.accent.teal.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.teal.subtlest.hovered'),
				borderColor: token('color.border.accent.teal'),
				textColor: token('color.text.accent.teal.bolder'),
				activeBackgroundColor: token('color.background.accent.teal.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'green-subtlest': {
		panelColor: '#E3FCEF',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#E3FCEF'],
				backgroundColor: token('color.background.accent.green.subtlest'),
				backgroundColorHovered: token('color.background.accent.green.subtlest.hovered'),
				borderColor: token('color.border.accent.green'),
				textColor: token('color.text.accent.green'),
				activeBackgroundColor: token('color.background.accent.green.subtler'),
				activeTextColor: token('color.text.accent.green.bolder'),
			}),
		},
	},
	'green-subtler': {
		panelColor: '#ABF5D1',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#ABF5D1'],
				backgroundColor: token('color.background.accent.green.subtler'),
				backgroundColorHovered: token('color.background.accent.green.subtler.hovered'),
				borderColor: token('color.border.accent.green'),
				textColor: token('color.text.accent.green'),
				activeBackgroundColor: token('color.background.accent.green.subtle'),
				activeTextColor: token('color.text.accent.green.bolder'),
			}),
		},
	},
	'green-subtle': {
		panelColor: '#97EDC9',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#97EDC9'],
				backgroundColor: token('color.background.accent.green.subtle'),
				backgroundColorHovered: token('color.background.accent.green.subtle.hovered'),
				borderColor: token('color.border.accent.green'),
				textColor: token('color.text.accent.green.bolder'),
				activeBackgroundColor: token('color.background.accent.green.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#97EDC9'],
				backgroundColor: token('color.background.accent.green.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.green.subtlest.hovered'),
				borderColor: token('color.border.accent.green'),
				textColor: token('color.text.accent.green.bolder'),
				activeBackgroundColor: token('color.background.accent.green.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'lime-subtlest': {
		panelColor: '#EFFFD6',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#EFFFD6'],
				backgroundColor: token('color.background.accent.lime.subtlest'),
				backgroundColorHovered: token('color.background.accent.lime.subtlest.hovered'),
				borderColor: token('color.border.accent.lime'),
				textColor: token('color.text.accent.lime'),
				activeBackgroundColor: token('color.background.accent.lime.subtler'),
				activeTextColor: token('color.text.accent.lime.bolder'),
			}),
		},
	},
	'lime-subtler': {
		panelColor: '#D3F1A7',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#D3F1A7'],
				backgroundColor: token('color.background.accent.lime.subtler'),
				backgroundColorHovered: token('color.background.accent.lime.subtler.hovered'),
				borderColor: token('color.border.accent.lime'),
				textColor: token('color.text.accent.lime'),
				activeBackgroundColor: token('color.background.accent.lime.subtle'),
				activeTextColor: token('color.text.accent.lime.bolder'),
			}),
		},
	},
	'lime-subtle': {
		panelColor: '#BDE97C',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#BDE97C'],
				backgroundColor: token('color.background.accent.lime.subtle'),
				backgroundColorHovered: token('color.background.accent.lime.subtle.hovered'),
				borderColor: token('color.border.accent.lime'),
				textColor: token('color.text.accent.lime.bolder'),
				activeBackgroundColor: token('color.background.accent.lime.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#BDE97C'],
				backgroundColor: token('color.background.accent.lime.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.lime.subtlest.hovered'),
				borderColor: token('color.border.accent.lime'),
				textColor: token('color.text.accent.lime.bolder'),
				activeBackgroundColor: token('color.background.accent.lime.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'yellow-subtlest': {
		panelColor: '#FFFAE6',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FFFAE6'],
				backgroundColor: token('color.background.accent.yellow.subtlest'),
				backgroundColorHovered: token('color.background.accent.yellow.subtlest.hovered'),
				borderColor: token('color.border.accent.yellow'),
				textColor: token('color.text.accent.yellow'),
				activeBackgroundColor: token('color.background.accent.yellow.subtler'),
				activeTextColor: token('color.text.accent.yellow.bolder'),
			}),
		},
	},
	'yellow-subtler': {
		panelColor: '#FFF0B3',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FFF0B3'],
				backgroundColor: token('color.background.accent.yellow.subtler'),
				backgroundColorHovered: token('color.background.accent.yellow.subtler.hovered'),
				borderColor: token('color.border.accent.yellow'),
				textColor: token('color.text.accent.yellow'),
				activeBackgroundColor: token('color.background.accent.yellow.subtle'),
				activeTextColor: token('color.text.accent.yellow.bolder'),
			}),
		},
	},
	'yellow-subtle': {
		panelColor: '#EFDD4E',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#EFDD4E'],
				backgroundColor: token('color.background.accent.yellow.subtle'),
				backgroundColorHovered: token('color.background.accent.yellow.subtle.hovered'),
				borderColor: token('color.border.accent.yellow'),
				textColor: token('color.text.accent.yellow.bolder'),
				activeBackgroundColor: token('color.background.accent.yellow.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#EFDD4E'],
				backgroundColor: token('color.background.accent.yellow.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.yellow.subtlest.hovered'),
				borderColor: token('color.border.accent.yellow'),
				textColor: token('color.text.accent.yellow.bolder'),
				activeBackgroundColor: token('color.background.accent.yellow.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'orange-subtlest': {
		panelColor: '#FFF5DB',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FFF5DB'],
				backgroundColor: token('color.background.accent.orange.subtlest'),
				backgroundColorHovered: token('color.background.accent.orange.subtlest.hovered'),
				borderColor: token('color.border.accent.orange'),
				textColor: token('color.text.accent.orange'),
				activeBackgroundColor: token('color.background.accent.orange.subtler'),
				activeTextColor: token('color.text.accent.orange.bolder'),
			}),
		},
	},
	'orange-subtler': {
		panelColor: '#FCE4A6',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FCE4A6'],
				backgroundColor: token('color.background.accent.orange.subtler'),
				backgroundColorHovered: token('color.background.accent.orange.subtler.hovered'),
				borderColor: token('color.border.accent.orange'),
				textColor: token('color.text.accent.orange'),
				activeBackgroundColor: token('color.background.accent.orange.subtle'),
				activeTextColor: token('color.text.accent.orange.bolder'),
			}),
		},
	},
	'orange-subtle': {
		panelColor: '#FBD779',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FBD779'],
				backgroundColor: token('color.background.accent.orange.subtle'),
				backgroundColorHovered: token('color.background.accent.orange.subtle.hovered'),
				borderColor: token('color.border.accent.orange'),
				textColor: token('color.text.accent.orange.bolder'),
				activeBackgroundColor: token('color.background.accent.orange.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FBD779'],
				backgroundColor: token('color.background.accent.orange.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.orange.subtlest.hovered'),
				borderColor: token('color.border.accent.orange'),
				textColor: token('color.text.accent.orange.bolder'),
				activeBackgroundColor: token('color.background.accent.orange.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'red-subtlest': {
		panelColor: '#FFEBE6',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FFEBE6'],
				backgroundColor: token('color.background.accent.red.subtlest'),
				backgroundColorHovered: token('color.background.accent.red.subtlest.hovered'),
				borderColor: token('color.border.accent.red'),
				textColor: token('color.text.accent.red'),
				activeBackgroundColor: token('color.background.accent.red.subtler'),
				activeTextColor: token('color.text.accent.red.bolder'),
			}),
		},
	},
	'red-subtler': {
		panelColor: '#FFBDAD',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FFBDAD'],
				backgroundColor: token('color.background.accent.red.subtler'),
				backgroundColorHovered: token('color.background.accent.red.subtler.hovered'),
				borderColor: token('color.border.accent.red'),
				textColor: token('color.text.accent.red'),
				activeBackgroundColor: token('color.background.accent.red.subtle'),
				activeTextColor: token('color.text.accent.red.bolder'),
			}),
		},
	},
	'red-subtle': {
		panelColor: '#FFB8B2',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FFB8B2'],
				backgroundColor: token('color.background.accent.red.subtle'),
				backgroundColorHovered: token('color.background.accent.red.subtle.hovered'),
				borderColor: token('color.border.accent.red'),
				textColor: token('color.text.accent.red.bolder'),
				activeBackgroundColor: token('color.background.accent.red.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FFB8B2'],
				backgroundColor: token('color.background.accent.red.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.red.subtlest.hovered'),
				borderColor: token('color.border.accent.red'),
				textColor: token('color.text.accent.red.bolder'),
				activeBackgroundColor: token('color.background.accent.red.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'magenta-subtlest': {
		panelColor: '#FFECF8',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FFECF8'],
				backgroundColor: token('color.background.accent.magenta.subtlest'),
				backgroundColorHovered: token('color.background.accent.magenta.subtlest.hovered'),
				borderColor: token('color.border.accent.magenta'),
				textColor: token('color.text.accent.magenta'),
				activeBackgroundColor: token('color.background.accent.magenta.subtler'),
				activeTextColor: token('color.text.accent.magenta.bolder'),
			}),
		},
	},
	'magenta-subtler': {
		panelColor: '#FDD0EC',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FDD0EC'],
				backgroundColor: token('color.background.accent.magenta.subtler'),
				backgroundColorHovered: token('color.background.accent.magenta.subtler.hovered'),
				borderColor: token('color.border.accent.magenta'),
				textColor: token('color.text.accent.magenta'),
				activeBackgroundColor: token('color.background.accent.magenta.subtle'),
				activeTextColor: token('color.text.accent.magenta.bolder'),
			}),
		},
	},
	'magenta-subtle': {
		panelColor: '#FCB6E1',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FCB6E1'],
				backgroundColor: token('color.background.accent.magenta.subtle'),
				backgroundColorHovered: token('color.background.accent.magenta.subtle.hovered'),
				borderColor: token('color.border.accent.magenta'),
				textColor: token('color.text.accent.magenta.bolder'),
				activeBackgroundColor: token('color.background.accent.magenta.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#FCB6E1'],
				backgroundColor: token('color.background.accent.magenta.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.magenta.subtlest.hovered'),
				borderColor: token('color.border.accent.magenta'),
				textColor: token('color.text.accent.magenta.bolder'),
				activeBackgroundColor: token('color.background.accent.magenta.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'purple-subtlest': {
		panelColor: '#EAE6FF',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#EAE6FF'],
				backgroundColor: token('color.background.accent.purple.subtlest'),
				backgroundColorHovered: token('color.background.accent.purple.subtlest.hovered'),
				borderColor: token('color.border.accent.purple'),
				textColor: token('color.text.accent.purple'),
				activeBackgroundColor: token('color.background.accent.purple.subtler'),
				activeTextColor: token('color.text.accent.purple.bolder'),
			}),
		},
	},
	'purple-subtler': {
		panelColor: '#C0B6F2',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#C0B6F2'],
				backgroundColor: token('color.background.accent.purple.subtler'),
				backgroundColorHovered: token('color.background.accent.purple.subtler.hovered'),
				borderColor: token('color.border.accent.purple'),
				textColor: token('color.text.accent.purple'),
				activeBackgroundColor: token('color.background.accent.purple.subtle'),
				activeTextColor: token('color.text.accent.purple.bolder'),
			}),
		},
	},
	'purple-subtle': {
		panelColor: '#E3BDFA',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#E3BDFA'],
				backgroundColor: token('color.background.accent.purple.subtle'),
				backgroundColorHovered: token('color.background.accent.purple.subtle.hovered'),
				borderColor: token('color.border.accent.purple'),
				textColor: token('color.text.accent.purple.bolder'),
				activeBackgroundColor: token('color.background.accent.purple.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#E3BDFA'],
				backgroundColor: token('color.background.accent.purple.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.purple.subtlest.hovered'),
				borderColor: token('color.border.accent.purple'),
				textColor: token('color.text.accent.purple.bolder'),
				activeBackgroundColor: token('color.background.accent.purple.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
	'gray-subtler': {
		panelColor: '#F4F5F7',
		appearances: {
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#F4F5F7'],
				backgroundColor: token('color.background.accent.gray.subtler'),
				backgroundColorHovered: token('color.background.accent.gray.subtler.hovered'),
				borderColor: token('color.border.accent.gray'),
				textColor: token('color.text.accent.gray'),
				activeBackgroundColor: token('color.background.accent.gray.subtle'),
				activeTextColor: token('color.text.accent.gray.bolder'),
			}),
		},
	},
	'gray-subtle': {
		panelColor: '#B7B9BE',
		appearances: {
			legacy: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#B7B9BE'],
				backgroundColor: token('color.background.accent.gray.subtle'),
				backgroundColorHovered: token('color.background.accent.gray.subtle.hovered'),
				borderColor: token('color.border.accent.gray'),
				textColor: token('color.text.accent.gray.bolder'),
				activeBackgroundColor: token('color.background.accent.gray.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
			standard: createTabColorAppearance({
				labelMessage: backgroundColorMessages['#B7B9BE'],
				backgroundColor: token('color.background.accent.gray.subtlest.pressed'),
				backgroundColorHovered: token('color.background.accent.gray.subtlest.hovered'),
				borderColor: token('color.border.accent.gray'),
				textColor: token('color.text.accent.gray.bolder'),
				activeBackgroundColor: token('color.background.accent.gray.bolder'),
				activeTextColor: token('color.text.inverse'),
			}),
		},
	},
};

const tabColorDefinitionsByKey = TAB_COLOR_DEFINITIONS as Partial<
	Record<string, TabColorDefinition>
>;
const panelColorToTabColor = new Map<string, TabColorKey>(
	Object.entries(TAB_COLOR_DEFINITIONS).map(([colorKey, definition]) => [
		definition.panelColor.toLowerCase(),
		colorKey as TabColorKey,
	]),
);

export const getTabColorAppearance = (
	color: TabColorKey,
	appearanceMode: TabColorAppearanceMode = 'legacy',
): TabColorAppearance => {
	const appearances: TabColorDefinition['appearances'] = TAB_COLOR_DEFINITIONS[color].appearances;
	return appearanceMode === 'legacy'
		? (appearances.legacy ?? appearances.standard)
		: appearances.standard;
};

export const getPanelColorForTabColor = (color: unknown): string | undefined => {
	if (typeof color !== 'string') {
		return undefined;
	}

	return tabColorDefinitionsByKey[color]?.panelColor;
};

export const getTabColorForPanelColor = (panelColor: unknown): TabColorKey | undefined => {
	if (typeof panelColor !== 'string') {
		return undefined;
	}

	return panelColorToTabColor.get(panelColor.toLowerCase());
};
